// ═══════════════════════════════════════════════════════════════════════════
// SPOTLIGHT — Recherche d'action en overlay centré (style Spotlight).
// Ouvert depuis la loupe du header (#header-search-btn, à côté du chatbot) ou au
// clavier (Ctrl / ⌘ + K). Remplace l'entrée de recherche du bandeau de nav.
// Réutilise mountSearchUI (moteur + rendu partagés avec la page #search).
// Ferme sur Échap ou clic hors du panneau ; se referme après ouverture d'une action.
// ═══════════════════════════════════════════════════════════════════════════

import { mountSearchUI } from './search-ui.js';

let overlay: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

function closeSpotlight(): void {
  if (!overlay) return;
  overlay.remove();
  overlay = null;
  // Rend le focus à l'élément déclencheur (accessibilité clavier).
  lastFocus?.focus?.();
  lastFocus = null;
}

function openSpotlight(): void {
  if (overlay) return;
  lastFocus = document.activeElement as HTMLElement | null;

  overlay = document.createElement('div');
  overlay.className = 'spotlight-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('pageSearch.title'));
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:700;background:rgba(15,23,32,.35);' +
    'display:flex;align-items:flex-start;justify-content:center;padding:12vh 16px 24px;';

  const card = document.createElement('div');
  card.style.cssText =
    'width:100%;max-width:640px;max-height:72vh;background:var(--bg2,#fff);' +
    'border:1px solid var(--border,#d5dbe3);border-radius:14px;' +
    'box-shadow:0 24px 60px rgba(0,0,0,.28);display:flex;flex-direction:column;overflow:hidden;';

  const body = document.createElement('div');
  body.style.cssText =
    'display:flex;flex-direction:column;min-height:0;overflow:auto;padding:8px 12px 12px;';

  card.appendChild(body);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // Clic sur le fond (hors panneau) = fermeture. mousedown pour éviter qu'une
  // sélection de texte finissant hors panneau ne ferme par accident.
  overlay.addEventListener('mousedown', (e) => {
    if (e.target === overlay) closeSpotlight();
  });

  mountSearchUI(body, { onPick: closeSpotlight, autofocus: true });
}

function onKeydown(e: KeyboardEvent): void {
  // Ctrl/⌘-K : bascule l'overlay.
  if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault();
    if (overlay) closeSpotlight();
    else openSpotlight();
    return;
  }
  if (e.key === 'Escape' && overlay) {
    e.preventDefault();
    closeSpotlight();
  }
}

function init(): void {
  const btn = document.getElementById('header-search-btn');
  if (btn) btn.addEventListener('click', () => openSpotlight());
  document.addEventListener('keydown', onKeydown);
  // Exposé pour d'éventuels appels programmés (parité avec les autres composants).
  (window as unknown as { openSpotlight?: () => void }).openSpotlight = openSpotlight;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export {};
